import type { AddressItem, BannerItem, GoodsCategory, GoodsItem, OrderItem, ShopConfig } from '@/types'
import { http } from 'uview-pro'
import { stripHtml } from '@/common/utils'

/** 分页响应 */
export interface PageResult<T> {
  list: T[]
  total: number
  page: number
  pageSize: number
}

/** 商品列表查询参数 */
export interface GoodsListParams {
  keyword?: string
  category?: Exclude<GoodsCategory, 'all'>
  sort?: 'hot' | 'points_asc' | 'points_desc'
  pointsMin?: number
  pointsMax?: number
  page?: number
  pageSize?: number
}

/** 兜底渐变（无主图商品卡片使用） */
const FALLBACK_GRADIENTS = [
  'linear-gradient(160deg, #ff6a5b 0%, #f0232f 100%)',
  'linear-gradient(160deg, #4facfe 0%, #2f6bff 100%)',
  'linear-gradient(160deg, #ffd45b 0%, #ff9500 100%)',
  'linear-gradient(160deg, #b58cff 0%, #7c3aed 100%)',
  'linear-gradient(160deg, #6ecbff 0%, #1e90ff 100%)',
]
const FALLBACK_EMOJI = '🎁'

function normalizeGoods(raw: any, index = 0): GoodsItem {
  // 图集解析：images 数组（元素内可能仍是逗号拼接串）/ img_list / 逗号分隔的 image，统一拍平
  let images: string[] = []
  if (Array.isArray(raw.images))
    images = raw.images
  else if (Array.isArray(raw.img_list) || Array.isArray(raw.imgList))
    images = raw.img_list ?? raw.imgList ?? []
  else if (raw.image)
    images = [raw.image]

  images = images
    .flatMap((s: any) => String(s).split(',')) // 元素内部按逗号再拆（后端会把多图拼成一个元素）
    .map((s: string) => s.trim())
    .filter(Boolean)
    // 过滤 Web 端误存的 blob: 预览地址（仅上传会话内有效，其他端无法加载）
    .filter(s => !s.startsWith('blob:'))

  return {
    ...raw,
    images,
    // 兑换前置条件（后端下发，布尔）
    needAddress: raw.needAddress === true,
    needAccount: raw.needAccount === true,
    // 主图取图集首图（图集被过滤光时回退空串走兜底样式）
    image: images[0] ?? '',
    // name 可能混入富文本标签，剥掉后展示纯文本
    name: stripHtml(raw.name),
    // 后端返回 snake_case（amount_text/category_label），统一映射为前端驼峰
    amountText: stripHtml(raw.amountText ?? raw.amount_text),
    categoryLabel: stripHtml(raw.categoryLabel ?? raw.category_label),
    gradient: raw.gradient || FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length],
    coverEmoji: raw.coverEmoji || FALLBACK_EMOJI,
  }
}

function normalizeOrder(raw: any): OrderItem {
  const addr = raw.address
  return {
    ...raw,
    orderNo: raw.orderNo ?? raw.order_no,
    goodsName: raw.goodsName ?? raw.goods_name,
    goodsAmountText: raw.goodsAmountText ?? raw.goods_amount_text,
    goodsTypeText: raw.goodsTypeText ?? raw.goods_type_text,
    goodsImage: raw.goodsImage ?? raw.goods_image,
    createTime: raw.createTime ?? raw.create_time,
    account: raw.account ?? '',
    userRemark: raw.userRemark ?? raw.user_remark ?? '',
    trackingNo: raw.trackingNo ?? raw.tracking_no ?? null,
    address: addr
      ? {
          name: addr.name,
          phone: addr.phone,
          full: addr.full ?? addr.full_address ?? '',
        }
      : null,
  }
}

function normalizeAddress(raw: any): AddressItem {
  return {
    ...raw,
    isDefault: raw.isDefault ?? Boolean(raw.is_default),
  }
}

/* ---------------- 商品 ---------------- */

/** 商品分类 */
export async function apiGetGoodsCategories(): Promise<Array<{ value: string, label: string }>> {
  return await http.get('/goods/categories')
}

/** 商品列表（首页） */
export async function apiGetGoodsList(params: GoodsListParams = {}): Promise<PageResult<GoodsItem>> {
  const page = await http.get<PageResult<any>>('/goods/list', params)
  // 兼容后端直接返回数组的情况
  const list = Array.isArray(page) ? page : (page.list || [])
  const normalized = list.map((item: any, i: number) => normalizeGoods(item, i))
  if (Array.isArray(page)) {
    return { list: normalized, total: normalized.length, page: 1, pageSize: normalized.length }
  }
  return { ...page, list: normalized }
}

/** 商品详情 */
export async function apiGetGoodsDetail(id: string): Promise<GoodsItem> {
  const goods = await http.get<any>(`/goods/${id}`)
  return normalizeGoods(goods)
}

/** 首页 Banner */
export async function apiGetBanners(): Promise<BannerItem[]> {
  return await http.get('/goods/banners')
}

/** 全局配置（客服电话/搜索热词） */
export async function apiGetShopConfig(): Promise<ShopConfig> {
  return await http.get('/goods/config')
}

/* ---------------- 积分 ---------------- */

/** 积分记录 */
export async function apiGetPointsRecords(params: { type?: 'income' | 'expense', page?: number, pageSize?: number } = {}): Promise<PageResult<any>> {
  const page = await http.get<any>('/points/records', params)
  const list = Array.isArray(page) ? page : (page.list || [])
  if (Array.isArray(page)) {
    return { list, total: list.length, page: 1, pageSize: list.length }
  }
  return { ...page, list }
}

/* ---------------- 收货地址 ---------------- */

/** 地址列表（默认地址排最前） */
export async function apiGetAddresses(): Promise<AddressItem[]> {
  const data = await http.get<any>('/addresses')
  return (Array.isArray(data) ? data : (data?.list || [])).map(normalizeAddress)
}

/** 新增地址 */
export async function apiAddAddress(address: Omit<AddressItem, 'id'>): Promise<AddressItem> {
  return normalizeAddress(await http.post<any>('/addresses', address))
}

/** 编辑地址（全量字段） */
export async function apiUpdateAddress(address: AddressItem): Promise<AddressItem> {
  return await http.put(`/addresses/${address.id}`, address)
}

/** 删除地址 */
export async function apiDeleteAddress(id: string): Promise<void> {
  await http.delete(`/addresses/${id}`)
}

/** 设为默认地址 */
export async function apiSetDefaultAddress(id: string): Promise<void> {
  await http.put(`/addresses/${id}/default`)
}

/* ---------------- 订单 ---------------- */

/** 创建兑换订单参数（needAccount 商品必填 account ≤64 字符；remark 选填 ≤200 字） */
export interface CreateOrderParams {
  goodsId: string
  addressId?: string
  account?: string
  remark?: string
}

/** 创建兑换订单（事务扣积分） */
export async function apiCreateOrder(params: CreateOrderParams): Promise<OrderItem> {
  return normalizeOrder(await http.post<any>('/orders', params))
}

/** 订单列表 */
export async function apiGetOrders(params: { status?: string, page?: number, pageSize?: number } = {}): Promise<PageResult<OrderItem>> {
  const page = await http.get<any>('/orders', params)
  const list = (Array.isArray(page) ? page : (page.list || [])).map(normalizeOrder)
  if (Array.isArray(page)) {
    return { list, total: list.length, page: 1, pageSize: list.length }
  }
  return { ...page, list }
}

/** 订单详情 */
export async function apiGetOrderDetail(id: string): Promise<OrderItem> {
  return normalizeOrder(await http.get<any>(`/orders/${id}`))
}

/** 取消兑换（退积分） */
export async function apiCancelOrder(id: string): Promise<void> {
  await http.put(`/orders/${id}/cancel`)
}

/** 确认收货 */
export async function apiReceiveOrder(id: string): Promise<void> {
  await http.put(`/orders/${id}/receive`)
}
